<template>
  <div
    class="status-bar"
    :class="`status-bar-license-blocks-${getLicenseBlocksNum || 1}`"
  >
    <q-resize-observer @resize="handleResize" />
    <div class="column status-bar-license-usage-container online-agent-activity">
      <div class="row justify-center">
        <div class="status-bar-license-usage-title gordita-medium-500-13">
          <div :data-testid="elementsIds.agents.statusBar.onlineAgentActivity.title">Online Agent Activity</div>
        </div>
      </div>
      <div :class="`status-bar-inner-container status-bar-online-agent-activity-container`">
        <status-bar-info-chart
          :size="size"
          :build-cache="isBuildAvoidanceLicensed(license?.windows?.summary)"
          style="flex-basis: 40%"
          chart="pie-chart"
          :datasets="onlineMachines.datasets"
          :legends="onlineMachines.legends"
          class="gadget-wrapper online-machines"
          :title="onlineMachines.title"
          :subtitle="onlineMachines.subtitle"
          :cloud="cloudStatus === CLOUD_STATUS.Enabled ? onlineMachines.cloud : undefined"
          cloud-title="Busy"
        />
        <status-bar-info-chart
          :size="size"
          :build-cache="isBuildAvoidanceLicensed(license?.windows?.summary)"
          cloud-title="Helper"
          :datasets="activeCores.datasets"
          style="flex-basis: 40%"
          chart="pie-chart"
          :legends="activeCores.legends"
          class="gadget-wrapper active-cores"
          :title="activeCores.title"
          :subtitle="activeCores.subtitle"
          :cloud="cloudStatus === CLOUD_STATUS.Enabled ? activeCores.cloud : undefined"
        />
        <status-bar-info-chart
          :size="size"
          :build-cache="isBuildAvoidanceLicensed(license?.windows?.summary)"
          style="flex-basis: 20%"
          class="gadget-wrapper builds-block"
          :title="'Builds'"
          :subtitle="agentsActivity?.builds"
        />
      </div>
    </div>

    <div class="column status-bar-license-usage-container license-usage">
      <div class="row justify-center">
        <div class="status-bar-license-usage-title gordita-medium-500-13">
          <div :data-testid="elementsIds.agents.statusBar.licenseUsage.title">Resource Overview</div>
        </div>
      </div>
      <div class="status-bar-inner-container status-bar-license-usage-container">
        <status-bar-license-info
          v-if="license?.windows?.status?.initiator?.all?.total"
          :test-id="'status-bar-license-info-initiator'"
          :show-title="!hasInitiatorFixedAndFloatingLicense || size === 'small'"
          class="license-info-block"
          :size="size"
          title="Initiators"
          style="flex: 1"
          :info="{
            available: license?.windows?.status?.initiator[String(initatorLicenseType).toLocaleLowerCase()].available,
            assigned: license?.windows?.status?.initiator[String(initatorLicenseType).toLocaleLowerCase()].assigned,
            cooldown: hasInitiatorLicense ? license?.windows?.status?.initiator[String(initatorLicenseType).toLocaleLowerCase()].cooldown : undefined,
            cooldownNextRelease: hasInitiatorLicense ? license?.windows?.status?.initiator[String(initatorLicenseType).toLocaleLowerCase()].cooldownNextRelease : undefined,
          }"
        >
          <template #tooltip>
            <legend-key-value
              test-id="status-bar-windows-license-info-initiator-all-assigned"
              :bold="true"
              text-color="white"
              :color="COLORS['System/app_secondary']"
              :text="$t('dashboard.top.topRightBars.legend.assigned')"
              :value="license?.windows?.status?.initiator?.all?.assigned"
            />
            <legend-key-value
              test-id="status-bar-windows-license-info-initiator-fixed-assigned"
              :legend="false"
              text-color="white"
              :color="COLORS['System/app_secondary']"
              :text="'Fixed'"
              :value="license?.windows?.status?.initiator?.fixed?.assigned"
              class="group-item-divider"
            />
            <legend-key-value
              test-id="status-bar-windows-license-info-initiator-floating-assigned"
              :legend="false"
              text-color="white"
              :color="COLORS['System/app_secondary']"
              :text="'Floating'"
              :value="license?.windows?.status?.initiator?.floating?.assigned"
            />
            <legend-key-value
              v-if="license?.windows?.status?.initiator?.all?.cooldown > 0"
              test-id="status-bar-windows-license-info-initiator-all-cooldown"
              :bold="true"
              text-color="white"
              :color="COLORS['Text/text_medium']"
              :text="$t('dashboard.top.topRightBars.legend.cooldown')"
              :value="license?.windows?.status?.initiator?.all?.cooldown"
            />
            <legend-key-value
              test-id="status-bar-windows-license-info-initiator-all-available"
              :bold="true"
              text-color="white"
              :color="COLORS['System/app_grey_89']"
              :text="$t('dashboard.top.topRightBars.legend.available')"
              :value="license?.windows?.status?.initiator?.all?.available"
            />
            <legend-key-value
              test-id="status-bar-windows-license-info-initiator-fixed-available"
              :legend="false"
              text-color="white"
              :color="COLORS['System/app_grey_89']"
              :text="'Fixed'"
              :value="license?.windows?.status?.initiator?.fixed?.available"
              class="group-item-divider"
            />
            <legend-key-value
              test-id="status-bar-windows-license-info-initiator-floating-available"
              :legend="false"
              text-color="white"
              :color="COLORS['System/app_grey_89']"
              :text="'Floating'"
              :value="license?.windows?.status?.initiator?.floating?.available"
            />
          </template>
          <template #header-postpend>
            <initiators-drop-down
              v-if="hasInitiatorFixedAndFloatingLicense && size !== 'small'"
              initiator-type="Initiators"
              :initiator-license-types-list="
                Object.keys(license?.windows?.status?.initiator)
                  .filter(status => status !== '__typename')
                  .map(status => status.charAt(0).toUpperCase() + status.slice(1))
              "
              @update-initiator-license-type="val => (initatorLicenseType = val)"
            />
          </template>
        </status-bar-license-info>
        <status-bar-license-info
          v-if="license?.windows?.status?.ciInitiator?.all?.total"
          :test-id="'status-bar-license-info-ci-initiator'"
          class="license-info-block"
          :show-title="!hasCIInitiatorFixedAndFloatingLicense || size === 'small'"
          :size="size"
          title="CI Initiators"
          style="flex: 1"
          :info="{
            available: license?.windows?.status?.ciInitiator[String(ciInitatorLicenseType).toLocaleLowerCase()].available,
            assigned: license?.windows?.status?.ciInitiator[String(ciInitatorLicenseType).toLocaleLowerCase()].assigned,
            cooldown: hasCIInitiatorLicense ? license?.windows?.status?.ciInitiator[String(ciInitatorLicenseType).toLocaleLowerCase()].cooldown : undefined,
            cooldownNextRelease: hasCIInitiatorLicense ? license?.windows?.status?.ciInitiator[String(ciInitatorLicenseType).toLocaleLowerCase()].cooldownNextRelease : undefined,
          }"
        >
          <template #tooltip>
            <legend-key-value
              test-id="status-bar-windows-license-info-ci-initiator-all-assigned"
              :bold="true"
              text-color="white"
              :color="COLORS['System/app_secondary']"
              :text="$t('dashboard.top.topRightBars.legend.assigned')"
              :value="license?.windows?.status?.ciInitiator?.all?.assigned"
            />
            <legend-key-value
              test-id="status-bar-windows-license-info-ci-initiator-fixed-assigned"
              :legend="false"
              text-color="white"
              :color="COLORS['System/app_secondary']"
              :text="'Fixed'"
              :value="license?.windows?.status?.ciInitiator?.fixed?.assigned"
              class="group-item-divider"
            />
            <legend-key-value
              test-id="status-bar-windows-license-info-ci-initiator-floating-assigned"
              :legend="false"
              text-color="white"
              :color="COLORS['System/app_secondary']"
              :text="'Floating'"
              :value="license?.windows?.status?.ciInitiator?.floating?.assigned"
            />
            <legend-key-value
              v-if="license?.windows?.status?.ciInitiator?.all?.cooldown > 0"
              test-id="status-bar-windows-license-info-ci-initiator-cooldown"
              :bold="true"
              text-color="white"
              :color="COLORS['Text/text_medium']"
              :text="$t('dashboard.top.topRightBars.legend.cooldown')"
              :value="license?.windows?.status?.ciInitiator?.all?.cooldown"
            />
            <legend-key-value
              test-id="status-bar-windows-license-info-ci-initiator-all-available"
              :bold="true"
              text-color="white"
              :color="COLORS['System/app_grey_89']"
              :text="$t('dashboard.top.topRightBars.legend.available')"
              :value="license?.windows?.status?.ciInitiator?.all?.available"
            />
            <legend-key-value
              test-id="status-bar-windows-license-info-ci-initiator-fixed-available"
              :legend="false"
              text-color="white"
              :color="COLORS['System/app_grey_89']"
              :text="'Fixed'"
              :value="license?.windows?.status?.ciInitiator?.fixed?.available"
              class="group-item-divider"
            />
            <legend-key-value
              :legend="false"
              text-color="white"
              :color="COLORS['System/app_grey_89']"
              :text="'Floating'"
              :value="license?.windows?.status?.ciInitiator?.floating?.available"
              test-id="status-bar-windows-license-info-ci-initiator-floating-available"
            />
          </template>
          <template #header-postpend>
            <initiators-drop-down
              v-if="hasCIInitiatorFixedAndFloatingLicense && size !== 'small'"
              initiator-type="CI Initiators"
              :initiator-license-types-list="
                Object.keys(license?.windows?.status?.ciInitiator)
                  .filter(status => status !== '__typename')
                  .map(status => status.charAt(0).toUpperCase() + status.slice(1))
              "
              @update-initiator-license-type="val => (ciInitatorLicenseType = val)"
            />
          </template>
        </status-bar-license-info>
        <status-bar-license-info
          v-if="hasBuildCacheLicense && !unlimitedCacheCores"
          :test-id="'status-bar-license-info-build-cache'"
          class="license-info-block"
          :size="size"
          title="Cache Cores"
          style="flex: 1"
          :info="{
            assigned: usedBuildCacheCores,
            available: availableBuildCacheCores,
          }"
          :next-reset-in="nextResetIn"
          :has-build-cache-license="hasBuildCacheLicense"
        />

        <status-bar-helper-cores-floating
          v-if="license?.windows?.status?.helperCores?.floating"
          class="license-info-block"
          style="flex: 1"
          :size="size"
        />
        <status-bar-license-info
          v-else
          :test-id="'status-bar-license-info-helper-cores'"
          class="license-info-block"
          :size="size"
          title="Helper Cores"
          :info="{
            assigned: license?.windows?.status?.helperCores?.fixed?.assigned,
            available: license?.windows?.status?.helperCores?.fixed?.available,
          }"
          style="flex: 1"
        />
        <status-bar-license-info
          v-if="cloudBlockVisible && cloudLicense?.licenseStatus?.toLowerCase() === CLOUD_LICENSE_STATUSES.Active && cloudLicense?.licenseType?.toLowerCase() !== CLOUD_LICENSE_TYPES.Trial"
          :test-id="'status-bar-license-info-cloud'"
          :size="size"
          title="Cloud Hours"
          :is-cloud-data="true"
          :num-of-blocks="getLicenseBlocksNum"
          :info="{
            assigned: totalLicenseConsumptionConverted,
            available: cloudLicense?.licenseDetails?.quantity - totalLicenseConsumptionConverted,
          }"
          style="flex: 1; justify-content: space-between"
        />

        <status-bar-cloud-license-info
          v-else-if="cloudStatus === CLOUD_STATUS.Enabled || cloudStatus === CLOUD_STATUS.Paused"
          :size="size"
          :cloud-license="cloudLicense"
          :num-of-blocks="getLicenseBlocksNum"
          style="flex: 1; justify-content: space-between"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { defineComponent, inject } from 'vue';
import { useAgentsPageQuery } from '@/graphql/codegen/generated/graphql';
import { CLOUD_STATUS } from '@/views/settings/settingsCloud/cloud.const';
import { IKCoordinatorsService } from '@/modules/CoordinatorsService/CoordinatorService.InjectionKeys';
import InitiatorsDropDown from '@/components/status-bar/components/InitiatorsDropDown.vue';
import { LICENSE_TYPES } from '@/const/licensePage';
import StatusBarCloudLicenseInfo from '@/components/status-bar/components/status-bar-cloud-license-info.vue';
import { CLOUD_LICENSE_STATUSES, CLOUD_LICENSE_TYPES } from '@/components/status-bar/components/CONST';
import elementsIds from '@/const/elementIds.json';
import { dateUtils } from '@/services/dateUtils';
import COLORS from '@/design-system/colors.json';
import StatusBarInfoChart from './components/status-bar-info-chart.vue';
import StatusBarLicenseInfo from './components/status-bar-license-info.vue';
import StatusBarHelperCoresFloating from './components/status-bar-helper-cores-floating.vue';
import LegendKeyValue from './components/legend-key-value.vue';

export default defineComponent({
  components: {
    StatusBarCloudLicenseInfo,
    StatusBarInfoChart,
    StatusBarLicenseInfo,
    StatusBarHelperCoresFloating,
    LegendKeyValue,
    InitiatorsDropDown,
  },
  setup() {
    const coordinatorsService = inject(IKCoordinatorsService);

    const GQL_AgentsPageQuery = useAgentsPageQuery(() => ({
      coordinatorId: coordinatorsService?.selectedCoordinator.value || '',
    }));
    return { GQL_AgentsPageQuery, LICENSE_TYPES, elementsIds, COLORS };
  },
  data() {
    return {
      elSize: { width: null, height: null },
      initatorLicenseType: 'All',
      ciInitatorLicenseType: 'All',
      CLOUD_STATUS,
      CLOUD_LICENSE_STATUSES,
      CLOUD_LICENSE_TYPES,
    };
  },
  computed: {
    ...mapGetters({
      pageSize: ['main/pageSize'],
    }),
    cloudStatus() {
      return this.GQL_AgentsPageQuery?.result?.value?.ibManagerEntities?.settings?.common?.cloud?.cloudStatus;
    },
    // totalLicenseConsumption we get in minutes, so we need to convert it to hours
    totalLicenseConsumptionConverted() {
      const totalLicenseConsumption = this.cloudLicense?.totalLicenseConsumption || this.cloudLicense?.totalLicenseConsumption === 0 ? Number(this.cloudLicense?.totalLicenseConsumption) : 0;
      const totalCacheConsumption = this.cloudLicense?.totalCacheConsumption || this.cloudLicense?.totalCacheConsumption === 0 ? Number(this.cloudLicense?.totalCacheConsumption) : 0;
      return Math.ceil((totalLicenseConsumption + totalCacheConsumption) / 60);
    },
    // the used amount needs to be rounded up and available to be rounded down(WIN-13188)
    usedBuildCacheCores() {
      return (this.license?.windows?.status?.totalBuildCacheCores || 0) - this.availableBuildCacheCores;
    },
    availableBuildCacheCores() {
      return Math.floor(this.license?.windows?.status?.remainingBuildCacheCores || 0);
    },
    cloudBlockVisible() {
      return this.cloudStatus === CLOUD_STATUS.Enabled || this.cloudStatus === CLOUD_STATUS.Paused || this.cloudStatus === CLOUD_STATUS.Deleting || this.cloudStatus === CLOUD_STATUS.InProcess;
    },
    license() {
      return this.GQL_AgentsPageQuery?.result?.value?.ibManagerEntities?.license || {};
    },
    cloudLicense() {
      return this.GQL_AgentsPageQuery?.result?.value?.ibManagerEntities.cloudLicense || {};
    },
    agentsActivityInfo() {
      return this.GQL_AgentsPageQuery?.result?.value?.ibManagerEntities?.agentsActiveInfo || {};
    },
    hasInitiatorLicense() {
      return this.license?.windows?.status?.initiator.fixed.total;
    },
    hasCIInitiatorLicense() {
      return this.license?.windows?.status?.ciInitiator.fixed.total;
    },
    hasBuildCacheLicense() {
      return this.GQL_AgentsPageQuery?.result?.value?.ibManagerEntities?.licenseMetadata?.hasBuildCache;
    },
    hasInitiatorFixedAndFloatingLicense() {
      return this.license?.windows?.status?.initiator.fixed.total > 0 && this.license?.windows?.status?.initiator.floating.total > 0;
    },
    hasCIInitiatorFixedAndFloatingLicense() {
      return this.license?.windows?.status?.ciInitiator.fixed.total > 0 && this.license?.windows?.status?.ciInitiator.floating.total > 0;
    },
    nextResetIn() {
      return dateUtils.getFormattedTime(this.license?.windows?.status?.buildCacheNextResetTime);
    },
    size() {
      const { width } = this.pageSize;
      if (width < 1360 - 79) return 'small'; // 79 is the width of the sidebar + padding
      if (width < 1599 - 79) return 'medium';
      return 'large';
    },
    agentsActivity() {
      return {
        activeCores: {
          onPrem: (this.agentsActivityInfo?.activeHelpers?.activeCoresRemote || 0) - (this.agentsActivityInfo?.activeHelpers?.activeCoresCloud || 0),
          cloud: this.agentsActivityInfo?.activeHelpers?.activeCoresCloud || 0,
          initiators: this.agentsActivityInfo?.activeHelpers?.activeCoresLocal || 0,
        },
        onlineAgentMachine: {
          online: this.agentsActivityInfo?.onlineHelpers?.onlineAgents || 0,
          active: this.agentsActivityInfo?.activeHelpers?.activeAgents || 0,
          idle: (this.agentsActivityInfo?.onlineHelpers?.onlineAgents || 0) - (this.agentsActivityInfo?.activeHelpers?.activeAgents || 0),
          cloud: this.agentsActivityInfo?.activeHelpers?.activeCloudAgents || 0,
          onPrem: (this.agentsActivityInfo?.activeHelpers?.activeAgents || 0) - (this.agentsActivityInfo?.activeHelpers?.activeCloudAgents || 0),
        },
        builds: this.agentsActivityInfo?.activeBuilds || 0,
      };
    },
    onlineMachines() {
      let legends;
      let cloud;
      switch (this.cloudStatus) {
        case CLOUD_STATUS.Enabled:
          legends = [
            {
              color: this.COLORS['Text/text_medium'],
              text: 'Idle',
              value: this.agentsActivity?.onlineAgentMachine?.idle,
            },
          ];
          cloud = {
            color: this.COLORS['System/app_secondary'],
            value: [
              {
                text: 'On Prem',
                value: this.agentsActivity?.onlineAgentMachine?.onPrem,
                color: this.COLORS['System/app_secondary'],
              },
              {
                text: 'Cloud',
                value: this.agentsActivity?.onlineAgentMachine?.cloud,
                color: this.COLORS['System/app_secondary'],
              },
            ],
          };
          return {
            title: 'Online Machines',
            subtitle: this.agentsActivity?.onlineAgentMachine?.online,
            datasets: this.getChartDataset([
              ...legends,
              {
                color: this.COLORS['System/app_secondary'],
                value: cloud.value.reduce((acc, cur) => acc + cur.value, 0),
              },
            ]),
            legends,
            cloud,
          };

        default:
          legends = [
            {
              color: this.COLORS['System/app_secondary'],
              text: 'Busy',
              value: this.agentsActivity?.onlineAgentMachine?.active,
            },
            {
              color: this.COLORS['Text/text_medium'],
              text: 'Idle',
              value: this.agentsActivity?.onlineAgentMachine?.idle,
            },
          ];
          return {
            title: 'Online Machines',
            subtitle: this.agentsActivity?.onlineAgentMachine?.online,
            datasets: this.getChartDataset(legends),
            legends,
          };
      }
    },
    activeCores() {
      let legends;
      let cloud;
      switch (this.cloudStatus) {
        case CLOUD_STATUS.Enabled:
          legends = [
            {
              text: 'Initiator',
              color: this.COLORS['Text/text_medium'],
              value: this.agentsActivity?.activeCores?.initiators,
            },
          ];
          cloud = {
            color: this.COLORS['System/app_secondary'],
            value: [
              {
                text: 'On Prem',
                value: this.agentsActivity?.activeCores?.onPrem,
                color: this.COLORS['System/app_secondary'],
              },
              {
                text: 'Cloud',
                value: this.agentsActivity?.activeCores?.cloud,
                color: this.COLORS['System/app_secondary'],
              },
            ],
          };
          return {
            title: 'Busy Cores',
            subtitle: [...legends, ...cloud.value].reduce((acc, legend) => acc + legend.value, 0),
            datasets: this.getChartDataset([
              ...legends,
              {
                color: this.COLORS['System/app_secondary'],
                value: cloud.value.reduce((acc, cur) => acc + cur.value, 0),
              },
            ]),
            cloud,
            legends,
          };

        default:
          legends = [
            {
              text: 'Helper',
              color: this.COLORS['System/app_secondary'],
              value: this.agentsActivity?.activeCores?.onPrem,
            },
            {
              text: 'Initiator',
              color: this.COLORS['Text/text_medium'],
              value: this.agentsActivity?.activeCores?.initiators,
            },
          ];
          return {
            title: 'Busy Cores',
            subtitle: legends.reduce((acc, legend) => acc + legend.value, 0),
            datasets: this.getChartDataset(legends),
            legends,
          };
      }
    },
    getLicenseBlocksNum() {
      // Number of license usage blocks
      const initiatorBlock = this.license?.windows?.status?.initiator?.all?.total > 0 || 0;
      const ciInitiatorBlock = this.license?.windows?.status?.ciInitiator?.all?.total > 0 || 0;
      const buildAvoidanceBlock = this.isBuildAvoidanceLicensed(this.license?.windows?.summary) || 0;
      const helperCoresBlock = this.license?.windows?.status?.helperCores?.floating > 0 || 1;
      const cloudBlock = this.cloudBlockVisible;
      return initiatorBlock + ciInitiatorBlock + buildAvoidanceBlock + helperCoresBlock + cloudBlock;
    },
    unlimitedCacheCores() {
      return this.license?.windows?.summary?.unlimitedCacheCores;
    },
  },
  methods: {
    handleResize(value) {
      this.elSize = value;
    },
    getChartDataset(legends) {
      const noData = legends.every(legend => legend.value === 0);
      return [
        {
          hoverBorderColor: 'white',
          hoverBackgroundColor: noData ? [this.COLORS['Text/text_medium']] : legends.map(legend => legend.color),
          backgroundColor: noData ? [this.COLORS['Text/text_medium']] : legends.map(legend => legend.color),
          data: noData ? [1] : legends.map(legend => legend.value),
        },
      ];
    },
  },
});
</script>

<style lang="scss" scoped>
.initator-license-type-drowpdown--header {
  :deep(.q-btn) {
    height: 32px;
    padding: 0 12px;
    width: 84px !important;
  }
}
.initator-license-type-drowpdown--info {
  display: flex;
  :deep(.q-btn) {
    height: 32px;
    padding: 0 12px;
    width: 84px !important;
  }
}
.status-bar {
  width: 100%;
  display: inline-flex;
  gap: 16px;
  max-width: 1624px;
  min-width: 830px;

  .status-bar--tooltip-icon {
    font-size: 16px;
    color: $-text-text-low;

    &:hover {
      color: $-text-text-low;
    }

    &.tooltip-active {
      color: $-text-text-low !important;
    }
  }

  @media only screen {
    // Flexes To All Resolutions
    &.status-bar-license-blocks-2 {
      .online-agent-activity {
        flex-basis: 55% !important;
      }
      .license-usage {
        flex-basis: 45% !important;
      }
    }
    &.status-bar-license-blocks-3 {
      .online-agent-activity {
        flex-basis: 45% !important;
      }
      .license-usage {
        flex-basis: 55% !important;
      }
    }
    &.status-bar-license-blocks-4 {
      .online-agent-activity {
        flex-basis: 38% !important;
      }
      .license-usage {
        flex-basis: 62% !important;
      }
    }
    &.status-bar-license-blocks-5 {
      .online-agent-activity {
        flex-basis: 33% !important;
      }
      .license-usage {
        flex-basis: 67% !important;
      }
    }
  }

  .status-bar-license-usage-container {
    position: relative;
    background: $-system-app-white;
    border: 1px solid $-system-app-border;
    height: 180px;
    flex-wrap: nowrap;
    @media only screen and (min-width: 1552px) and (max-width: 1920px) {
      &.online-agent-activity {
        min-width: 432px;
      }
      .online-machines,
      .active-cores {
        min-width: 166px;
      }
      .builds-block {
        min-width: 100px;
      }
      .license-info-block {
        min-width: 180px;
      }
    }
    @media only screen and (min-width: 1360px) and (max-width: 1551px) {
      &.online-agent-activity {
        min-width: 400px;
      }
      .online-machines,
      .active-cores {
        min-width: 166px;
      }
      .builds-block {
        min-width: 84px;
      }
      .license-info-block {
        min-width: 164px;
      }
    }
    @media only screen and (min-width: 950px) and (max-width: 1359px) {
      &.online-agent-activity {
        min-width: 358px;
      }
      .online-machines,
      .active-cores {
        min-width: 142px;
      }
      .builds-block {
        min-width: 72px;
      }
    }

    .status-bar-license-usage-title {
      height: 24px;
      display: flex;
      align-items: center;
      gap: 6.5px;
      flex-grow: 0;
      padding: 3.5px 12px;
      background: $-system-app-grey-89;
      border-radius: 0px 0px 10px 10px;
      color: $-text-text-medium;
      margin-bottom: 12px;

      > i {
        cursor: pointer;
      }
    }

    .status-bar-inner-container {
      height: 132px;
      flex-wrap: nowrap;
      margin-bottom: 12px;
      display: flex;
      border: unset;
      flex-grow: 1;
      > *:not(:first-child) {
        border-left: 1px solid $-system-app-border;
      }
    }
  }
}
.group-item-divider {
  margin: -4px 0 -4px 0;
}
</style>
